/**
 * File API
 *
 * This module provides functions for secure file access via signed URLs:
 * - getSignedUrl: Get a signed URL for any object key
 * - getLogoUrl: Get a signed URL for a company logo
 * - getDocumentUrl: Get a signed URL for a PO document
 *
 * All files are stored in MinIO/S3 and accessed via time-limited signed URLs.
 */

import api from './index';

/**
 * Get a signed URL for a file by its object key
 *
 * @param {string} objectKey - The S3 object key
 * @returns {Promise<Object>} Response with signed URL
 *
 * @example
 * const result = await getSignedUrl('companyId/documents/po-123.pdf');
 * // result.data.signedUrl - Temporary URL to access the file
 * // result.data.expiresIn - Seconds until URL expires
 */
export const getSignedUrl = (objectKey) => {
  return api.get('/files/signed-url', {
    params: { objectKey }
  });
};

/**
 * Get a signed URL for a company logo
 *
 * @param {string} companyId - The company ID
 * @returns {Promise<Object>} Response with signed URL
 *
 * @example
 * const result = await getLogoUrl('507f1f77bcf86cd799439011');
 * // result.data.signedUrl - Temporary URL to access the logo (1 hour expiry)
 */
export const getLogoUrl = (companyId) => {
  return api.get(`/files/logo/${companyId}`);
};

/**
 * Get a signed URL for a purchase order document
 *
 * @param {string} poId - The purchase order ID
 * @returns {Promise<Object>} Response with signed URL
 *
 * @example
 * const result = await getDocumentUrl('507f1f77bcf86cd799439011');
 * // result.data.signedUrl - Temporary URL to access the document (15 min expiry)
 * // result.data.mimeType - MIME type of the document
 */
export const getDocumentUrl = (poId) => {
  return api.get(`/files/document/${poId}`);
};

/**
 * Build a proxy URL for loading files directly through the backend.
 * This avoids CORS issues when loading PDFs or other files in the browser.
 *
 * @param {string} objectKey - The S3 object key
 * @returns {string} Full proxy URL including authentication token
 *
 * @example
 * const url = buildProxyUrl('companyId/documents/po-123.pdf');
 * // Use this URL directly in <img src> or react-pdf Document file prop
 */
export const buildProxyUrl = (objectKey) => {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  const token = localStorage.getItem('token') || '';
  return `${baseUrl}/files/proxy?objectKey=${encodeURIComponent(objectKey)}&token=${encodeURIComponent(token)}`;
};

/**
 * Get a text preview of a non-visual file (Excel, EDI, CSV, text).
 *
 * @param {string} objectKey - The S3 object key
 * @returns {Promise<Object>} Response with { text, format }
 */
export const getPreviewText = (objectKey) => {
  return api.get('/files/preview-text', {
    params: { objectKey }
  });
};
